package org.example.project.enginevertrieb

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun VertriebLeadtypenDialog(
    onClose: () -> Unit,
    onChanged: (List<VertriebsindexApi.LeadTypEintrag>) -> Unit,
    zIndex: Int = POPUP_ENGINE_MODAL_Z_INDEX,
    rollenKey: String = "Vertriebsindex"
) {
    val singular = "Lead-Typ"
    val plural = "Leadtypen"
    var leadtypen by remember { mutableStateOf<List<VertriebsindexApi.LeadTypEintrag>>(emptyList()) }
    var flows by remember { mutableStateOf<List<All>>(emptyList()) }
    var selected by remember { mutableStateOf<Set<String>>(emptySet()) }
    var method by remember { mutableStateOf("CALL") }
    var assigning by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var version by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    suspend fun reload() {
        try {
            leadtypen = VertriebsindexApi.loadLeadtypen().sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
            flows = FlowProfileApi.load()
            onChanged(leadtypen)
            error = ""
            version++
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            error = e.message ?: "Leadtypen konnten nicht geladen werden."
        }
    }

    LaunchedEffect(Unit) { reload() }

    PopupEngineCard(
        windowKey = "vertrieb-leadtypen-verwalten",
        zIndex = zIndex,
        widthPx = 880, heightPx = 640, minWidthPx = 560, minHeightPx = 420,
        centered = true, viewportAnchored = true, resizable = true,
        onClose = onClose
    ) {
        Div({
            attr("role", "dialog"); attr("aria-modal", "true")
            style {
                width(100.percent); height(100.percent); padding(18.px); boxSizing("border-box")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                color(DashboardTheme.TEXT_LIGHT); gap(10.px)
            }
        }) {
            Div({ style { textAlign("center"); fontSize(20.px); fontWeight("700") } }) { Text("$plural verwalten") }
            if (error.isNotBlank()) Div({ attr("role", "alert"); style { color(Color("#ffb4b4")); textAlign("center") } }) { Text(error) }
            key(version) {
                TabularScreen(
                    screenKey = "VertriebLeadtypen",
                    rollenKey = rollenKey,
                    title = null,
                    headers = listOf("#", singular, "Call", "DM"),
                    fixedLeadingColumns = listOf("#"),
                    models = leadtypen.map { All(id = it.id, leadTyp = it.leadTyp) },
                    data = leadtypen.map { type -> type.id to listOf(type.id, type.leadTyp, flows.firstOrNull { it.id == type.flowProfileIds["CALL"] }?.vertriebCallName.orEmpty(), flows.firstOrNull { it.id == type.flowProfileIds["DM"] }?.vertriebCallName.orEmpty()) },
                    editable = true,
                    commitChangedRowsOnly = true,
                    includeMetadata = false,
                    allowImportButton = false,
                    allowExportButton = false,
                    allowSettingsButton = false,
                    createButtonLabel = "$singular anlegen",
                    createRow = { id -> listOf(id, "", "", "") },
                    nonEditableCols = setOf("#", "Call", "DM"),
                    onSelectedRowsChange = { selected = it },
                    extraNavButtons = listOf(
                        TabularToolbarButton("Methode", "images/filter.svg", !assigning,
                            dropdownOptions = listOf("CALL" to "Call", "DM" to "DM"), selectedDropdownValue = method,
                            onDropdownChange = { if (it != null) method = it }),
                        TabularToolbarButton("Flow-Profil", "images/call.svg", !assigning && selected.isNotEmpty(),
                            dropdownOptions = listOf("" to "Leer") + flows.map { it.id to it.vertriebCallName },
                            selectedDropdownValue = leadtypen.filter { it.id in selected }.map { it.flowProfileIds[method].orEmpty() }.distinct().singleOrNull(),
                            dropdownPlaceholder = "Flow-Profil zuweisen", onDropdownChange = { id ->
                                if (id != null && selected.isNotEmpty()) {
                                    val ids = selected.toList(); val targetMethod = method
                                    assigning = true
                                    scope.launch {
                                        try { leadtypen = VertriebsindexApi.setLeadTypeFlow(ids, targetMethod, id); onChanged(leadtypen); error = ""; LinkedInSyncState.version++; version++ }
                                        catch (e: CancellationException) { throw e }
                                        catch (e: Exception) { error = e.message ?: "Flow-Zuordnung konnte nicht gespeichert werden." }
                                        finally { assigning = false }
                                    }
                                }
                            })
                    ),
                    onRefresh = { scope.launch { reload() } },
                    onCommit = {},
                    onCommitConfirmed = { rows, complete ->
                        scope.launch {
                            try {
                                val changed = rows.map { (rowId, columns) ->
                                    val knownId = rowId.takeIf { id -> leadtypen.any { it.id == id } }.orEmpty()
                                    VertriebsindexApi.LeadTypEintrag(knownId, columns.getOrNull(1).orEmpty().trim())
                                }
                                val saved = VertriebsindexApi.saveLeadtypen(changed)
                                leadtypen = saved.sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
                                onChanged(leadtypen)
                                error = ""
                                complete(true)
                                version++
                            } catch (e: CancellationException) {
                                complete(false)
                                throw e
                            } catch (e: Exception) {
                                error = e.message ?: "Leadtypen konnten nicht gespeichert werden."
                                complete(false)
                            }
                        }
                    },
                    onDeleteRows = { ids ->
                        try {
                            leadtypen = VertriebsindexApi.deleteLeadtypen(ids).sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
                            onChanged(leadtypen)
                            error = ""
                            version++
                        } catch (e: CancellationException) { throw e }
                        catch (e: Exception) {
                            val marker = Regex("vertrieb_leadtyp_in_verwendung:(\\d+)").find(e.message.orEmpty())
                            error = if (marker != null) {
                                "Löschen nicht möglich: Dieser Leadtyp wird noch ${marker.groupValues[1]} Leads zugewiesen."
                            } else e.message ?: "Leadtypen konnten nicht gelöscht werden."
                            throw e
                        }
                    }
                )
            }
        }
    }
}
